import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'

export interface RequestConfig<TData = unknown> extends AxiosRequestConfig<TData> {
  /**
   * Skip automatic Authorization header injection for this call.
   */
  skipAuth?: boolean
}

export interface ApiErrorPayload {
  error?: string
  message?: string
  status?: number
  code?: string
  errors?: unknown
  [key: string]: unknown
}

export class ApiError extends Error {
  status?: number
  code?: string
  details?: unknown
  cause?: unknown

  constructor(message: string, status?: number, code?: string, details?: unknown, cause?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.cause = cause
  }
}

type AuthTokenResolver = () => string | null | undefined

let authTokenResolver: AuthTokenResolver | null = null

/**
 * Provide a function that returns the current auth token.
 * This keeps the HTTP layer decoupled from where the token lives.
 */
export function setAuthTokenResolver(resolver: AuthTokenResolver | null) {
  authTokenResolver = resolver
}

const baseURL = import.meta.env.VITE_API_URL

if (!baseURL) {
  console.warn('[API] Missing VITE_API_URL env value, falling back to relative URLs')
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: baseURL || undefined,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use((config) => {
  const typedConfig = config as InternalAxiosRequestConfig & RequestConfig

  typedConfig.headers = AxiosHeaders.from(typedConfig.headers ?? {})
  const headers = typedConfig.headers as AxiosHeaders

  if (typedConfig.skipAuth !== true && authTokenResolver) {
    const token = authTokenResolver()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
  }

  return typedConfig
})

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(normalizeError(error))
  },
)

export async function request<T = unknown, TData = unknown>(config: RequestConfig<TData>) {
  const response = await apiClient.request<T>(config)
  return response.data
}

function normalizeError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorPayload>
    const status = axiosError.response?.status
    const payload = axiosError.response?.data
    const message = payload?.error || payload?.message || axiosError.message || 'Request failed'
    const normalized = new ApiError(message, status, payload?.code || axiosError.code, payload?.errors ?? payload, error)
    return normalized
  }

  if (error instanceof Error) {
    return new ApiError(error.message, undefined, undefined, undefined, error)
  }

  return new ApiError('Unknown error')
}
